import {
    CostCenter,
    Employee,
    ExpenseStatusEnum,
    RolesEnum,
    Vendor,
    Workflow,
    WorkflowConditionEnum,
    WorkflowRule,
    WorkflowScopeEnum,
    WorkflowTriggerEnum,
    Expense,
} from "@finway-group/shared/lib/models"
import { WorkflowRuleApprover } from "@finway-group/shared/lib/models/workflow/workflowRuleApprover"
import { ALL_TRIGGER_OBJECTS, RESPONSIBLE_USER_APPROVER, SUPERIOR_APPROVER } from "Components/forms/workflowForm/workflow.form"
import i18n from "Shared/locales/i18n"
import { CostCenterService, CurrencyService, EmployeeService, UserService } from "Shared/services"
import { isPendingExpense } from "../expenses/expenseUtils"

export const inititalWorkflowData = (costCenters: CostCenter[], vendors: Vendor[], employees: Employee[]) => {
    const triggerType = costCenters.length > 0 ? WorkflowTriggerEnum.COST_CENTER : vendors.length > 0 ? WorkflowTriggerEnum.VENDOR : WorkflowTriggerEnum.USER

    return new Workflow({
        triggerType,
        triggerReferenceIds: [],
        scope: WorkflowScopeEnum.ALL,
        rules: [
            {
                order: 1,
                approverData: undefined,
            },
        ],
    })
}

export const adjustWorkflowInitData = (workflowData: any) => ({
    ...workflowData,
    triggerReferenceIds: workflowData.isGeneralized ? [ALL_TRIGGER_OBJECTS] : workflowData.triggerReferenceIds,
    rules: workflowData.rules.map((rule: any) => ({
        ...rule,
        approverData: rule.approverData.isSuperior ? SUPERIOR_APPROVER : rule.approverData.isResponsibleEmployee ? RESPONSIBLE_USER_APPROVER : rule.approverData.user,
        ...(rule.secondApprover
            ? {
                  secondApprover: rule.secondApprover?.isSuperior
                      ? SUPERIOR_APPROVER
                      : rule.secondApprover.isResponsibleEmployee
                      ? RESPONSIBLE_USER_APPROVER
                      : rule.secondApprover.user,
              }
            : {}),
    })),
})

export const determineApplicableWorkflow = (expense: any, workflows: Workflow[], isInvoiceRequest: boolean, showGrossAmount: boolean) => {
    const filteredWorkflows = workflows
        ?.filter((workflow: Workflow) => {
            switch (workflow.triggerType) {
                case WorkflowTriggerEnum.COST_CENTER:
                    return (workflow.isGeneralized && expense.costCenter) || workflow.triggerReferenceIds.includes(expense.costCenter)
                case WorkflowTriggerEnum.VENDOR:
                    return (workflow.isGeneralized && expense.vendor) || workflow.triggerReferenceIds.includes(expense.vendor)
                case WorkflowTriggerEnum.USER:
                    return (workflow.isGeneralized && expense.requestedBy) || workflow.triggerReferenceIds.includes(expense.requestedBy)
                case WorkflowTriggerEnum.COST_CENTER_2:
                    return (workflow.isGeneralized && expense.costCenter2) || workflow.triggerReferenceIds.includes(expense.costCenter2)
                case WorkflowTriggerEnum.AMOUNT:
                    const convertedAbsoluteExpenseValue = Math.abs(
                        CurrencyService.convert(showGrossAmount ? expense.totalGrossPrice : expense.totalNetPrice, expense.currency, workflow.triggerReferenceCurrency)
                    )
                    return convertedAbsoluteExpenseValue > Number(workflow.triggerReferenceValue)
                default:
                    return false
            }
        })
        .filter((workflow: Workflow) => {
            switch (workflow.scope) {
                case WorkflowScopeEnum.ALL:
                    return true
                case WorkflowScopeEnum.REIMBURSEMENT:
                    return expense.isReimbursement
                case WorkflowScopeEnum.PURCHASE:
                    return !isInvoiceRequest && !expense.isReimbursement
                case WorkflowScopeEnum.INVOICE:
                    return isInvoiceRequest && !expense.isReimbursement
            }
        })
        .filter((workflow: Workflow) => {
            // filter out any workflows that only have the current requester
            // as the approver since this won't work
            return workflow.rules.length > 1 || workflow.rules[0]?.approverData !== expense.requestedBy
        })
        .sort((workflowA: any, workflowB: any) => workflowA.order - workflowB.order)

    // determine if also the first rule is applicable, if not, move on to next one
    for (const determinedWorkflow of filteredWorkflows) {
        const firstFittingRule = getFirstFittingRule(expense, determinedWorkflow)

        // this means we found the best fitting workflow
        if (firstFittingRule) return determinedWorkflow
    }

    return undefined
}

export const getActiveWorkflowRule = (expense: Expense, workflows: Workflow[]) => {
    const workflow = getWorkflowForExpense(expense, workflows)
    const activeRule = workflow?.rules.find(({ _id }: WorkflowRule) => _id === expense.activeWorkflowRule)

    return { activeRule, workflow }
}

export const getWorkflowForExpense = (expense: any, workflows: Workflow[]) => expense.workflow && workflows.find(({ _id }: any) => _id === expense.workflow)

export const getFirstFittingRule = (expense: any, workflow: Workflow) => {
    let ruleOrder = 1
    let firstRule = workflow.rules.find((rule: WorkflowRule) => rule.order === ruleOrder)

    // skip rules if the approver equals the requester
    while (firstRule && shouldSkipRule(firstRule, expense)) {
        ruleOrder++
        firstRule = workflow.rules.find((rule: WorkflowRule) => rule.order === ruleOrder)
    }

    return firstRule
}

export const shouldSkipRule = (rule: WorkflowRule, expense: any) => {
    // check amount conflict
    if (rule.condition === WorkflowConditionEnum.IF) {
        const convertedAbsoluteExpenseAmount = Math.abs(CurrencyService.convert(expense.totalNetPrice, expense.currency, rule.currency))
        if (convertedAbsoluteExpenseAmount <= rule.amount) return true
    }

    let firstRuleApproverId = rule.approverData.user
    if (!firstRuleApproverId) {
        if (rule.approverData.isResponsibleEmployee) {
            firstRuleApproverId = CostCenterService.getCostCenterById(expense.costCenter)?.responsibleUser
        } else if (rule.approverData.isSuperior) {
            firstRuleApproverId = EmployeeService.getEmployeeById(expense.requestedBy)?.activeCompanyProfile?.superior
            if (!firstRuleApproverId) {
                const adminUsers = EmployeeService.getEmployees().filter((user) => user.activeCompanyProfile.isAdmin)
                firstRuleApproverId = adminUsers.find((user) => user.id !== expense.requestedBy)?.id || EmployeeService.getAutoApprover()
            }
        }
    }

    let secondRuleApproverId = rule.secondApprover?.user
    if (!secondRuleApproverId && rule.secondApprover) {
        if (rule.secondApprover.isResponsibleEmployee) {
            secondRuleApproverId = CostCenterService.getCostCenterById(expense.costCenter)?.responsibleUser
        } else if (rule.secondApprover.isSuperior) {
            secondRuleApproverId = EmployeeService.getEmployeeById(expense.requestedBy)?.activeCompanyProfile?.superior
            if (!secondRuleApproverId) {
                const adminUsers = EmployeeService.getEmployees().filter((user) => user.activeCompanyProfile.isAdmin)
                secondRuleApproverId = adminUsers.find((user) => user.id !== expense.requestedBy)?.id || EmployeeService.getAutoApprover()
            }
        }
    }

    if ((!secondRuleApproverId && firstRuleApproverId === expense.requestedBy) || (firstRuleApproverId === secondRuleApproverId && firstRuleApproverId === expense.requestedBy)) {
        return true
    }

    // If requester is admin and has no superior set and rule needs superior, skip rule entirely, even if there is an "AND" condition on the rule.
    if (rule.approverData.isSuperior || rule.secondApprover?.isSuperior) {
        const requestingEmployee = EmployeeService.getEmployeeById(expense.requestedBy) as Employee
        const isRequesterAdmin = requestingEmployee?.activeCompanyProfile?.role === RolesEnum.ADMIN
        const requesterHasSuperior = requestingEmployee?.activeCompanyProfile?.superior
        const ruleRequiresSuperior = rule.approverData?.isSuperior || rule.secondApprover?.isSuperior
        if (ruleRequiresSuperior && isRequesterAdmin && !requesterHasSuperior) return true
    }

    return false
}

export const shouldShowWorkflowIndicator = (expense: Expense, workflows: Workflow[]) => {
    const workflow = getWorkflowForExpense(expense, workflows)

    if (!workflow) return false

    switch (workflow?.scope) {
        case WorkflowScopeEnum.ALL:
            return isPendingExpense(expense)
        case WorkflowScopeEnum.REIMBURSEMENT:
            return expense.isReimbursement && isPendingExpense(expense)
        case WorkflowScopeEnum.PURCHASE:
            return expense.status === ExpenseStatusEnum.PURCHASE_PENDING
        case WorkflowScopeEnum.INVOICE:
            return expense.status === ExpenseStatusEnum.INVOICE_PENDING
        default:
            return false
    }
}

export const getWorkflowById = (id: string | undefined, workflows: Workflow[]) => {
    if (!id) return undefined
    else return workflows.find(({ _id }: Workflow) => _id === id)
}

export const isUserPartOfExpenseWorkflow = (expense: Expense, workflows: Workflow[], userId: string) => {
    const rules = getWorkflowForExpense(expense, workflows)?.rules || []
    return rules.some(
        (rule: WorkflowRule) =>
            // check for approver user match
            rule.approverData.user === userId ||
            rule.secondApprover?.user === userId ||
            // check for superior match
            ((rule.approverData.isSuperior || rule.secondApprover?.isSuperior) && UserService.getUserById(expense.requestedBy)?.superior === userId) ||
            // check for cost center match
            ((rule.approverData.isResponsibleEmployee || rule.secondApprover?.isResponsibleEmployee) &&
                CostCenterService.getCostCenterById(expense.costCenter || "")?.responsibleUser === userId)
    )
}

export const convertApproverFormData = (formApprover: any) =>
    formApprover === SUPERIOR_APPROVER
        ? { isSuperior: true }
        : formApprover === RESPONSIBLE_USER_APPROVER
        ? { isResponsibleEmployee: true }
        : formApprover
        ? { user: formApprover }
        : undefined

export const getApproverLabelData = (approverData: any) => {
    if (!approverData) return undefined

    const autoApprover = EmployeeService.getAutoApprover()

    if (approverData.isSuperior)
        return {
            label: i18n.t("input:workflow.superior"),
            user: autoApprover,
        }

    if (approverData.isResponsibleEmployee)
        return {
            label: i18n.t("input:workflow.responsible_employee"),
            user: autoApprover,
        }

    const approverObject = EmployeeService.getEmployeeById(approverData.user)

    return {
        label: `${approverObject?.firstName} ${approverObject?.lastName}`,
        user: approverObject,
    }
}

export const getApproverForRule = (approverData: WorkflowRuleApprover | undefined, expense: Expense) => {
    if (!approverData) return undefined

    if (approverData.isSuperior) {
        // When approver requires superior, and the user has no superior
        // Put the first alphabetically admin as the approver
        const requester = UserService.getUserById(expense.requestedBy)
        const superior = requester?.activeCompanyProfile?.superior

        if (!superior) {
            if (requester?.activeCompanyProfile?.role === RolesEnum.APPROVER || requester?.activeCompanyProfile?.role === RolesEnum.EMPLOYEE) {
                // If user is employee or approver but has no superior set, apply 1st alphabetically admin
                const sortedAdmins = EmployeeService.getEmployees(true)
                    .filter((e) => e.activeCompanyProfile?.role === RolesEnum.ADMIN)
                    .sort((a, b) => {
                        return a.firstName.localeCompare(b.firstName)
                    })

                return sortedAdmins?.[0]?.id
            }
        } else {
            return superior
        }
    }

    if (approverData.isResponsibleEmployee) return CostCenterService.getCostCenterById(expense.costCenter || "")?.responsibleUser

    return approverData.user
}

export const buildWorkflowUpdateData = (expense: Expense, workflow: Workflow) => {
    const firstRule = getFirstFittingRule(expense, workflow)
    const approvalNeededBy = getApproverForRule(firstRule?.approverData, expense)
    if (!approvalNeededBy) return {}

    // only set the second approver if it wouldn't be the same as the first one
    const secondApprover = getApproverForRule(firstRule?.secondApprover, expense)
    const secondApprovalNeededBy = secondApprover !== approvalNeededBy ? secondApprover : undefined

    const didWorkflowChange = workflow._id !== expense.workflow || firstRule?._id !== expense.activeWorkflowRule

    return {
        workflow: didWorkflowChange ? workflow._id : expense.workflow,
        activeWorkflowRule: didWorkflowChange ? firstRule?._id : expense.activeWorkflowRule,
        approvalNeededBy,
        secondApprovalNeededBy,
        approvalsForActiveWorkflowRule: didWorkflowChange ? [] : expense.approvalsForActiveWorkflowRule,
    }
}

export const buildUnsetWorkflowUpdateData = () => ({
    // This fields should be set as "null" instead of "undefined" because undefined fields are removed after stringification, hence it would be troublesome sending them to the back end. Furthermore, mongo will skip updating undefined fields.
    workflow: null,
    activeWorkflowRule: null,
    secondApprovalNeededBy: null,
    approvalsForActiveWorkflowRule: [],
})

const isIfConditionMetForRule = (expense: Expense, rule: any) => {
    const convertedAbsoluteExpenseAmount = Math.abs(CurrencyService.convert(expense.totalNetPrice, expense.currency, rule.currency))
    return convertedAbsoluteExpenseAmount > rule.amount
}

export const getAllRemainingApprovers = (workflows: Workflow[], expense: Expense) => {
    let allApprovers: string[] = []
    const workflow = getWorkflowById(expense.workflow, workflows)
    if (!workflow) return allApprovers
    const { activeRule } = getActiveWorkflowRule(expense, workflows)

    for (const workflowRule of workflow.rules) {
        if (activeRule && activeRule.order > workflowRule.order) continue

        const approverId = getApproverForRule(workflowRule.approverData, expense)

        switch (workflowRule.condition) {
            case WorkflowConditionEnum.AND:
            case WorkflowConditionEnum.OR:
                const secondApproverId = getApproverForRule(workflowRule.secondApprover, expense)
                allApprovers.push(approverId, secondApproverId)
                break
            case WorkflowConditionEnum.IF:
                const isConditionMet = isIfConditionMetForRule(expense, workflowRule)
                if (isConditionMet) {
                    allApprovers.push(approverId)
                }
                break
            default:
                allApprovers.push(approverId)
                break
        }
    }
    return allApprovers
}
